import { useEffect } from 'react'
import { createContext, useState, useMemo, ReactNode, useRef } from 'react'
import { GenericStoreContextType } from '@/shared/types/generic'
import { persistApplicationState } from '@/utils/store/features/application/applicationSlice'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '@/utils/store/store'

const GenericStoreContext = createContext<GenericStoreContextType>({
    store: {},
    setGenericStoreValue: () => { },
    getGenericStoreValue: () => undefined,
    clearGenericStore: () => { },
    removeGenericStoreValue: () => { },
    getAllGenericStore: () => ({}),
})

const GenericStoreProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [store, setStore] = useState<Record<string, unknown>>({})
    const storeRef = useRef<Record<string, unknown>>({})
    const applicationState = useSelector((state: RootState) => state.application)

    const dispatch = useDispatch()

    useEffect(() => {
        if (applicationState) {
            Object.entries(applicationState).forEach(([key, value]) => {
                setGenericStoreValue(key, value)
            })
        }
        // runs once when the provider mounts: it takes over the application state of that moment
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const setGenericStoreValue = (key: string, value: unknown, persist?: boolean) => {
        const existing = storeRef.current[key]
        const shouldMerge =
            existing && typeof existing === 'object' && !Array.isArray(existing) &&
            value && typeof value === 'object' && !Array.isArray(value)
        const mergedValue = shouldMerge ? { ...existing as Record<string, unknown>, ...value as Record<string, unknown> } : value
        const nextStore = { ...storeRef.current, [key]: mergedValue }

        storeRef.current = nextStore
        setStore(nextStore)

        if (persist)
            dispatch(persistApplicationState({ [key]: mergedValue }))
    }

    const getGenericStoreValue = <T = unknown>(key: string): T | undefined => {
        return store[key] as T
    }

    const clearGenericStore = () => {
        storeRef.current = {}
        setStore({})
    }

    const removeGenericStoreValue = (key: string, persist?: boolean) => {
        const remaining = { ...storeRef.current }
        delete remaining[key]

        storeRef.current = remaining
        setStore(remaining)

        if (persist)
            dispatch(persistApplicationState({ [key]: undefined }))
    }

    const getAllGenericStore = () => store

    // the functions only use the ref, the setter and dispatch (and getGenericStoreValue the store): the value changes with the store
    const contextValue = useMemo(() => ({
        store,
        setGenericStoreValue,
        getGenericStoreValue,
        clearGenericStore,
        removeGenericStoreValue,
        getAllGenericStore,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [store])

    return (
        <GenericStoreContext.Provider value={contextValue}>
            {children}
        </GenericStoreContext.Provider>
    )
}

export { GenericStoreProvider, GenericStoreContext }
